import { registerActions } from "../actionRegistry";
/**
 * Image Request / Brief Functions
 *
 * CRUD operations for image requests (briefs) sent to Pictufy.
 */

import { api } from "../api";
import { showLoadingOverlay, hideLoadingOverlay, toastSuccess, toastError } from "../ui/messages";
import { modal, closeModalLast, refreshModal } from "../ui/modal";
import { getDomElement, setHtml, fadeOut, h } from "../utils/dom";

export function deletebrief(id: string): void {
  if (!confirm("Are you sure you want to delete this image request?")) return;
  api.briefs
    .delete({ id })
    .then(() => {
      refreshModal();
      toastSuccess("Image request deleted");
    })
    .catch((err: unknown) => {
      toastError((err as Error).message);
    });
}

// Both of these own their error handling, so the `void` at their call sites is
// genuine fire-and-forget. Previously neither caught, and the `void` was what hid
// the failure: a server error deleted nothing and reported nothing.
export async function briefDeleteFile(file_id: string, brief_id: string): Promise<void> {
  try {
    await api.briefs.deleteFile({ id: file_id, brief_id });
  } catch (err: unknown) {
    toastError((err as Error).message);
    return;
  }
  toastSuccess("File was deleted");
  await briefGetFiles(brief_id);
}

/**
 * One row in a brief's attachment list.
 *
 * Shared with `uploadCallbacks.briefAddFile`, which used to hand-build the same
 * five elements as a second template literal — two renderers of one row, kept in
 * step by eye. `filename` is DB-stored and was interpolated into `innerHTML`
 * unescaped by both; here it is appended, so it is a text node.
 */
export function briefFileRow(file: {
  fileId: string;
  briefId: string;
  filename: string;
  iconClass: string;
}): HTMLElement {
  const trash = h("span", "brief-file-trash", h("i", "far fa-trash-alt"));
  trash.addEventListener("click", () => void briefDeleteFile(file.fileId, file.briefId));

  const row = h(
    "span",
    "brief-file",
    h("span", "brief-file-icon", h("i", `far ${file.iconClass}`)),
    h("span", "brief-file-name", file.filename),
    trash
  );
  row.id = `brief-file-${file.fileId}`;
  return row;
}

export async function briefGetFiles(brief_id: string): Promise<void> {
  let response;
  try {
    response = await api.briefs.getFiles({ id: brief_id });
  } catch (err: unknown) {
    toastError((err as Error).message);
    return;
  }
  const container = getDomElement("brief-files");
  if (!container) return;

  const files = response.files ?? [];
  if (files.length === 0) {
    setHtml("brief-files", "<i>No files uploaded</i>");
  } else {
    // Each row closes over its own `f`, so the ids no longer travel out as
    // `data-file-id` / `data-brief-id` and back in via a `#brief-files
    // .brief-file-trash` sweep — a round-trip that also needed two non-null
    // assertions to convince tsc the attributes it had just written were there.
    container.replaceChildren(
      ...files.map((f) =>
        briefFileRow({
          fileId: f.id,
          briefId: brief_id,
          filename: f.filename,
          iconClass: f.iconClass,
        })
      )
    );
  }
}
export async function briefinprogress(brief_id: string): Promise<void> {
  await api.briefs.setInProgress({ id: brief_id });
  location.reload();
}

export async function briefdeliverred(brief_id: string): Promise<void> {
  await api.briefs.deliver({ id: brief_id });
  fadeOut("brief-" + brief_id);
  toastSuccess("Brief is marked as delivered");
}

export async function newbrief(): Promise<void> {
  try {
    const response = await api.briefs.add();
    modal("editbrief:" + response.brief_id, "dynamic");
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function savebrief(brief_id: string): Promise<void> {
  showLoadingOverlay("Sending your request...");

  const titleEl = getDomElement<HTMLInputElement>("brief-title");
  const descEl = getDomElement<HTMLTextAreaElement>("brief-description");

  try {
    await api.briefs.save({
      id: brief_id,
      title: titleEl?.value ?? "",
      desc: descEl?.value ?? "",
    });
    closeModalLast();
    setTimeout(() => {
      modal("image-request-sent", "small");
    }, 1000);
  } catch (err: unknown) {
    toastError((err as Error).message);
  } finally {
    hideLoadingOverlay();
  }
}

registerActions({
  deletebrief,
  briefinprogress,
  briefdeliverred,
});
